import { useEffect, useState } from "react";
import { School, Plus, Users, ClipboardList, Sparkles, Loader2, Zap } from "lucide-react";
import { listClassrooms } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OptIn } from "./instructor/OptIn";
import { CreateClassModal } from "./instructor/CreateClassModal";
import { ClassroomDetail } from "./instructor/ClassroomDetail";

// InstructorPage is now a thin orchestrator: role gate → class list → detail.
// All sub-components live under /pages/instructor/* (iter-35 split).
export const InstructorPage = ({ onPreviewLecture, onGoCreate }) => {
  const { user, refresh: refreshAuth } = useAuth();
  const [classrooms, setClassrooms] = useState(null);
  const [selected, setSelected] = useState(null);
  const [createModal, setCreateModal] = useState(false);

  const isInstructor = user && (user.role === "instructor" || user.role === "admin");

  const load = () => listClassrooms().then(setClassrooms).catch(() => setClassrooms([]));
  useEffect(() => { if (isInstructor) load(); }, [isInstructor]);

  if (!user) return null;
  if (!isInstructor) return <OptIn onDone={() => refreshAuth().then(load)} />;

  if (selected) return (
    <div className="max-w-4xl mx-auto">
      <ClassroomDetail
        classroom={selected}
        onBack={() => { setSelected(null); load(); }}
        onPreviewLecture={onPreviewLecture}
        onGoCreate={onGoCreate}
      />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2"><School className="h-7 w-7 text-accent" /> Your Classes</h1>
          <p className="mt-1 text-muted-foreground">Create a class, share the code, post assignments, and track every student.</p>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && (
            <a
              href="/lti/demo"
              data-testid="open-lti-demo"
              className="inline-flex items-center gap-2 rounded-full border-2 border-accent/40 bg-accent/5 px-4 py-2.5 font-semibold hover:bg-accent/10 transition text-accent"
              title="Canvas LTI 1.3 mock demo"
            >
              <Zap className="h-4 w-4" /> Canvas LTI
              <span className="ml-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">Mock</span>
            </a>
          )}
          <button data-testid="new-class-btn" onClick={() => setCreateModal(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 font-semibold hover:opacity-90 transition">
            <Plus className="h-4 w-4" /> New class
          </button>
        </div>
      </div>

      {classrooms === null ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : classrooms.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-border p-12 text-center">
          <Sparkles className="h-8 w-8 text-accent mx-auto" />
          <p className="mt-4 font-serif text-xl">No classes yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Create your first class in 10 seconds.</p>
          <button onClick={() => setCreateModal(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 font-semibold hover:opacity-90 transition">
            <Plus className="h-4 w-4" /> New class
          </button>
        </div>
      ) : (
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {classrooms.map((c) => (
            <li key={c.id} data-testid={`class-${c.id}`}>
              <button onClick={() => setSelected(c)}
                      className="w-full text-left rounded-2xl border border-border bg-card p-5 hover:border-accent/60 transition">
                <p className="font-serif text-xl font-bold">{c.name}</p>
                {c.subject && <p className="text-xs text-muted-foreground mt-0.5">{c.subject}</p>}
                <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.roster_count}</span>
                  <span className="flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {c.assignment_count}</span>
                  <span className="ml-auto font-mono tracking-wider text-accent">{c.code}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <CreateClassModal open={createModal} onClose={() => setCreateModal(false)}
                        onCreated={() => load()} />
    </div>
  );
};
